import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { UserGear, FloppyDisk } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIES = [
  { key: 'new', label: 'New Members (N)', hint: 'Naye customers jinke paas MB assign nahi hua' },
  { key: 'inquiry', label: 'Inquiry Leads', hint: 'Inquiry link se aane wale guests' },
  { key: 'support_guest', label: 'Support Guests', hint: 'Bina ID ke support chats' },
];

/**
 * P8 — Chat Assign Settings. Assign exactly ONE handler to each incoming-chat
 * category. A handler cannot hold two categories (enforced here + on backend).
 * Admins always see everything; already-registered MB customers using
 * Trouble-Sign-In recovery are unaffected.
 */
const ChatAssignSettings = ({ adminId }) => {
  const [assignments, setAssignments] = useState({ new: '', inquiry: '', support_guest: '' });
  const [handlers, setHandlers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/chat-assign/settings`);
      const a = res.data?.assignments || {};
      setAssignments({ new: a.new || '', inquiry: a.inquiry || '', support_guest: a.support_guest || '' });
      setHandlers(res.data?.handlers || []);
    } catch (_) { toast.error('Failed to load chat assignments'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const setCat = (cat, hid) => setAssignments((prev) => ({ ...prev, [cat]: hid }));

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/chat-assign/settings`, {
        actor_id: adminId,
        assignments: {
          new: assignments.new || null,
          inquiry: assignments.inquiry || null,
          support_guest: assignments.support_guest || null,
        },
      });
      toast.success('Chat assignments saved');
      const a = res.data?.assignments || {};
      setAssignments({ new: a.new || '', inquiry: a.inquiry || '', support_guest: a.support_guest || '' });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const hName = (h) => h.personal_name || h.username || 'Handler';
  const roleShort = (r) => (r || '').replace('_handler', '').replace('third_party_withdraw', 'third-party');

  if (loading) return <div className="p-6 text-center text-sm text-slate-400">Loading…</div>;

  return (
    <div className="p-4 space-y-4" data-testid="chat-assign-settings">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center text-indigo-600 flex-shrink-0">
          <UserGear size={22} weight="bold" />
        </div>
        <div>
          <h3 className="text-base font-bold text-slate-800">Chat Assign Settings</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Har category ke liye ek handler set karo. Ek handler ko teeno category
            de sakte ho, par ek category do handlers ko nahi jaati.
            Admin ko sab dikhta hai; MB-assigned customers (Trouble Sign In) affect nahi honge.
          </p>
        </div>
      </div>

      <div className="space-y-3">
        {CATEGORIES.map((c) => (
          <div key={c.key} className="bg-white border border-slate-200 rounded-xl p-3" data-testid={`chat-assign-row-${c.key}`}>
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="text-sm font-semibold text-slate-800">{c.label}</div>
                <div className="text-[11px] text-slate-400">{c.hint}</div>
              </div>
            </div>
            <select
              value={assignments[c.key]}
              onChange={(e) => setCat(c.key, e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white focus:outline-none focus:border-indigo-500"
              data-testid={`chat-assign-select-${c.key}`}
            >
              <option value="">— Unassigned (everyone sees) —</option>
              {handlers.map((h) => (
                <option key={h.id} value={h.id}>
                  {hName(h)} · {roleShort(h.role)}
                </option>
              ))}
            </select>
          </div>
        ))}
      </div>

      <button
        onClick={save}
        disabled={saving}
        className="w-full py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
        data-testid="chat-assign-save-btn"
      >
        <FloppyDisk size={16} weight="bold" />
        {saving ? 'Saving…' : 'Save Assignments'}
      </button>
    </div>
  );
};

export default ChatAssignSettings;
